import {
  ACTION_EVENTS_ADD,
  ACTION_EVENTS_ADDING,
  ACTION_EVENTS_ADD_ERROR,
} from "./add";
import { ACTION_EVENTS_ADD_RESET } from "./reset-add";
import {
  ACTION_EVENTS_UPDATE,
  ACTION_EVENTS_UPDATE_ERROR,
  ACTION_EVENTS_UPDATING,
} from "./update";
import { ACTION_EVENTS_UPDATE_RESET } from "./reset-update";
import {
  ACTION_EVENTS_DELETE,
  ACTION_EVENTS_DELETE_ERROR,
  ACTION_EVENTS_DELETE_RESET,
  ACTION_EVENTS_DELETING,
} from "./delete";
import {
  ACTION_EVENTS_ALLOW_EDIT_ALLOWING,
  ACTION_EVENTS_ALLOW_EDIT,
  ACTION_EVENTS_ALLOW_EDIT_ERROR,
} from "./allow-edit";
import {
  ACTION_EVENTS_LIST_LOADING,
  ACTION_EVENTS_LIST_LOADED,
  ACTION_EVENTS_LIST_ERRORED,
} from "./load";
import { StoreError } from "../../errors";
import {
  IdResourceState,
  buildIdResourceActionTypes,
  buildIdResourceActions,
  buildIdResourceReducer,
  buildIdResourceSelectors,
  initialIdResourceState,
} from "../../util/id-resource";

import {
  ACTION_EVENTS_LISTEN_CHANGE,
  ACTION_EVENTS_LISTEN_REMOVE,
} from "./listen-change"; //Also listens for event changes
import { AnyAction } from "redux";

export type ReminderMode = "off" | "secondary" | "notes" | "both";

export type TastingEvent = {
  id: string;
  owner: string | undefined | null;
  name: string | undefined | null;
  venue: string | undefined | null;
  date: Date | undefined | null;
  price: string | undefined | null;
  related: (string | null)[] | undefined | null;
  bartender: string | undefined | null;
  tasters: (string | null)[] | undefined | null;
  beers: (string | null)[] | undefined | null;
  asterisksAllowed: number | undefined | null;
  doubleAsterisk?: boolean | undefined | null;
  editingAllowed: boolean | undefined | null;
  rounds: number | undefined | null;
  reminderMode?: ReminderMode | null;
  lottoLocoEnabled?: boolean | undefined | null;

  //Helper Methods
  formattedDate: () => string;
  formattedMonth: () => string;
};

/**
 * The events slice carries two distinct resources: the per-event item (loaded
 * via `loadItem(id)` and managed by the id-resource primitives) and the
 * "related events list" loaded via `load()` for the current user. The list
 * has no id, so it gets its own request-token guard right here.
 *
 * Legacy fields (`items` array, `itemsLoading`, `itemsError`) are mirrored
 * from the new id-resource shape so existing screens keep working without
 * being touched in this PR.
 */
export type TastingEventsState = IdResourceState<TastingEvent> & {
  // Legacy fields — derived mirrors of the id-resource state, kept until every
  // consumer has been migrated to the selectors below.
  items: TastingEvent[] | null;
  itemsLoading: { [key: string]: boolean };
  itemsError: { [key: string]: StoreError };
  // List-level state for `load()` (fetches all related events for the user).
  loading: boolean;
  loaded: boolean;
  error: StoreError | null;
  /** Bumped at the start of every list-level fetch; the reducer ignores any
   *  list-level commit whose token doesn't match. Same staleness guard the
   *  id-resource primitives use, applied to the list flow. */
  listRequestToken: number;
  // Other sub-states (unchanged):
  add: {
    adding: boolean;
    added: TastingEvent | null;
    error: StoreError | null;
  };
  update: {
    updating: boolean;
    updated: TastingEvent | null;
    error: StoreError | null;
  };
  allowEditingAllowing: { [key: string]: boolean };
  allowEditingError: { [key: string]: StoreError };
  delete: {
    deleting: { [key: string]: boolean };
    deleted: string | null;
    error: StoreError | null;
  };
};

export const EVENTS_ACTION_TYPES = buildIdResourceActionTypes("events");
export const eventsActions = buildIdResourceActions<TastingEvent>(
  EVENTS_ACTION_TYPES
);
export const eventsSelectors = buildIdResourceSelectors<
  { events: TastingEventsState },
  TastingEvent
>((root) => root.events);

const idResourceReducer = buildIdResourceReducer<
  TastingEvent,
  TastingEventsState
>(EVENTS_ACTION_TYPES);

const initialState: TastingEventsState = {
  ...initialIdResourceState<TastingEvent>(),
  items: null,
  itemsLoading: {},
  itemsError: {},
  loading: false,
  loaded: false,
  error: null,
  listRequestToken: 0,
  add: { adding: false, added: null, error: null },
  update: { updating: false, updated: null, error: null },
  allowEditingAllowing: {},
  allowEditingError: {},
  delete: { deleting: {}, deleted: null, error: null },
};

const sortItems = (i1: TastingEvent, i2: TastingEvent): number => {
  return (i2?.date?.getTime() ?? 0) - (i1?.date?.getTime() ?? 0);
};

const itemsArrayFromMap = (
  itemsById: Record<string, TastingEvent> | undefined
): TastingEvent[] =>
  Object.values(itemsById ?? {})
    .filter((i): i is TastingEvent => Boolean(i))
    .sort(sortItems);

/** Set of action types whose handlers touched itemsById and therefore need
 *  the legacy `items` array mirror to be regenerated from the canonical map.
 *  Other actions (e.g. unrelated sub-state changes, `@@INIT`, the legacy
 *  test-injected state pass-through) leave `items` alone — important so a
 *  test mock that supplies `items: [...]` without populating `itemsById`
 *  still renders correctly. */
const ITEMSBYID_TOUCHING_ACTIONS = new Set<string>([
  EVENTS_ACTION_TYPES.startedLoading,
  EVENTS_ACTION_TYPES.loaded,
  EVENTS_ACTION_TYPES.errored,
  EVENTS_ACTION_TYPES.removed,
  ACTION_EVENTS_LIST_LOADING,
  ACTION_EVENTS_LIST_LOADED,
  ACTION_EVENTS_LIST_ERRORED,
  ACTION_EVENTS_LISTEN_CHANGE,
  ACTION_EVENTS_LISTEN_REMOVE,
  ACTION_EVENTS_ADD,
  ACTION_EVENTS_UPDATE,
  ACTION_EVENTS_ALLOW_EDIT,
  ACTION_EVENTS_DELETE,
]);

/** Mirror the id-resource state's loadingFor / errorFor / itemsById onto the
 *  legacy fields. Only invoked for actions that actually modified itemsById.
 *
 *  Subtlety around `items`: the Events screen derives its spinner via
 *  `loading && !items`, and JS evaluates `![]` as `false`. An empty `[]`
 *  mirror during the first fetch would suppress the spinner and flash a
 *  "No events" empty state before the data lands. We keep `items` at `null`
 *  until something has been committed once, then it sticks as an array so
 *  post-commit empty states (e.g. an empty list loads, or the last event is
 *  removed) render correctly.
 *
 *  When `state.itemsById` is undefined (a test mock injecting the legacy
 *  shape directly), we pass `state.items` through untouched. */
const reflectLegacyMirrors = (
  state: TastingEventsState
): TastingEventsState => {
  let items: TastingEvent[] | null;
  if (!state.itemsById) {
    items = state.items;
  } else {
    const hasAnyItem = Object.keys(state.itemsById).length > 0;
    const previouslyCommitted =
      state.items !== null && state.items !== undefined;
    items =
      state.loaded || hasAnyItem || previouslyCommitted
        ? itemsArrayFromMap(state.itemsById)
        : null;
  }
  return {
    ...state,
    items,
    itemsLoading: state.loadingFor ?? state.itemsLoading ?? {},
    itemsError: state.errorFor
      ? (Object.fromEntries(
          Object.entries(state.errorFor).filter(([, e]) => e !== null)
        ) as { [key: string]: StoreError })
      : state.itemsError ?? {},
  };
};

const commit = (
  state: TastingEventsState,
  item: TastingEvent
): TastingEventsState => ({
  ...state,
  itemsById: { ...state.itemsById, [item.id]: item },
});

/** A completed write supersedes any read that began before it. */
const finishItemMutation = (
  state: TastingEventsState,
  id: string
): TastingEventsState => ({
  ...state,
  requestToken: { ...state.requestToken, [id]: (state.requestToken[id] ?? 0) + 1 },
  loadingFor: { ...state.loadingFor, [id]: false },
  errorFor: { ...state.errorFor, [id]: null },
});

const drop = (
  state: TastingEventsState,
  id: string
): TastingEventsState => {
  const { [id]: _removed, ...rest } = state.itemsById;
  return { ...state, itemsById: rest };
};

export default function eventsReducer(
  state: TastingEventsState = initialState,
  action: AnyAction
): TastingEventsState {
  // Run the id-resource cases first (per-item loading / loaded / errored /
  // removed). The staleness guard for per-item lookups lives there.
  let next = idResourceReducer(state, action);

  switch (action.type) {
    case ACTION_EVENTS_LIST_LOADING: {
      const { listRequestToken } = action.payload as {
        listRequestToken: number;
      };
      next = {
        ...next,
        loading: true,
        error: null,
        listRequestToken,
      };
      break;
    }
    case ACTION_EVENTS_LIST_LOADED: {
      const { items, listRequestToken } = action.payload as {
        items: TastingEvent[];
        listRequestToken: number;
      };
      // Drop stale list responses — same idea as the per-id token guard.
      if (state.listRequestToken !== listRequestToken) return next;
      const itemsById: Record<string, TastingEvent> = {};
      for (const item of items) itemsById[item.id] = item;
      next = {
        ...next,
        itemsById,
        loading: false,
        loaded: true,
        error: null,
      };
      break;
    }
    case ACTION_EVENTS_LIST_ERRORED: {
      const { error, listRequestToken } = action.payload as {
        error: StoreError;
        listRequestToken: number;
      };
      if (state.listRequestToken !== listRequestToken) return next;
      next = { ...next, loading: false, error, itemsById: {} };
      break;
    }
    case ACTION_EVENTS_LISTEN_CHANGE: {
      next = commit(next, action.payload as TastingEvent);
      break;
    }
    case ACTION_EVENTS_LISTEN_REMOVE: {
      next = drop(next, action.payload as string);
      break;
    }
    case ACTION_EVENTS_ADDING: {
      next = { ...next, add: { ...next.add, adding: action.payload } };
      break;
    }
    case ACTION_EVENTS_ADD: {
      const item = action.payload as TastingEvent;
      next = commit(next, item);
      next = { ...next, add: { ...next.add, added: item, error: null } };
      break;
    }
    case ACTION_EVENTS_ADD_ERROR: {
      next = { ...next, add: { ...next.add, error: action.payload } };
      break;
    }
    case ACTION_EVENTS_ADD_RESET: {
      next = { ...next, add: { adding: false, added: null, error: null } };
      break;
    }
    case ACTION_EVENTS_UPDATING: {
      next = { ...next, update: { ...next.update, updating: action.payload } };
      break;
    }
    case ACTION_EVENTS_UPDATE: {
      const item = action.payload as TastingEvent;
      next = commit(finishItemMutation(next, item.id), item);
      next = { ...next, update: { ...next.update, updated: item, error: null } };
      break;
    }
    case ACTION_EVENTS_UPDATE_ERROR: {
      next = { ...next, update: { ...next.update, error: action.payload } };
      break;
    }
    case ACTION_EVENTS_UPDATE_RESET: {
      next = { ...next, update: { updating: false, updated: null, error: null } };
      break;
    }
    case ACTION_EVENTS_ALLOW_EDIT_ALLOWING: {
      const { id, allowing } = action.payload as {
        id: string;
        allowing: boolean;
      };
      next = {
        ...next,
        allowEditingAllowing: { ...next.allowEditingAllowing, [id]: allowing },
      };
      break;
    }
    case ACTION_EVENTS_ALLOW_EDIT: {
      const { id, allowed } = action.payload as {
        id: string;
        allowed: boolean;
      };
      const existing = next.itemsById[id];
      if (existing) {
        next = commit(next, { ...existing, editingAllowed: allowed });
      }
      break;
    }
    case ACTION_EVENTS_ALLOW_EDIT_ERROR: {
      const { id, error } = action.payload as { id: string; error: StoreError };
      next = {
        ...next,
        allowEditingError: { ...next.allowEditingError, [id]: error },
      };
      break;
    }
    case ACTION_EVENTS_DELETING: {
      const { id, deleting } = action.payload as {
        id: string;
        deleting: boolean;
      };
      next = {
        ...next,
        delete: {
          ...next.delete,
          deleting: { ...next.delete.deleting, [id]: deleting },
        },
      };
      break;
    }
    case ACTION_EVENTS_DELETE: {
      const itemId = action.payload as string;
      next = drop(finishItemMutation(next, itemId), itemId);
      next = {
        ...next,
        delete: { ...next.delete, deleted: itemId, error: null },
      };
      break;
    }
    case ACTION_EVENTS_DELETE_ERROR: {
      const { error } = action.payload as { error: StoreError };
      next = { ...next, delete: { ...next.delete, error } };
      break;
    }
    case ACTION_EVENTS_DELETE_RESET: {
      next = { ...next, delete: { deleting: {}, deleted: null, error: null } };
      break;
    }
    default:
      break;
  }

  // Only regenerate legacy mirrors when the action actually touched itemsById.
  // Other actions (sub-state-only changes, @@INIT, test-state pass-through)
  // leave the legacy fields alone so existing consumers keep working.
  if (ITEMSBYID_TOUCHING_ACTIONS.has(action.type)) {
    return reflectLegacyMirrors(next);
  }
  return next;
}
